import React, { useCallback, useEffect } from 'react';
import { reportError } from '../../lib/reportError';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useErrorModal } from '../../hooks/useErrorModal';
import {
  useAddressesQuery,
  useCreateAddressMutation,
  useEditAddressMutation,
  RestAddressAttributesData
} from '../../hooks/useAddress';
import {
  useUpdateCartMutation,
  useCart,
  CartUpdatePropertyName,
  CartUpdateAction
} from '../../hooks/useCart';
import { CheckoutShippingAddressContent } from './CheckoutShippingAddressContent';
import { ShippingAddressLoading } from './ShippingAddressLoading';
import './CheckoutShippingAddress.scss';

export const CheckoutShippingAddress: React.FC = () => {
  const { showErrorModal } = useErrorModal();
  const defaultAddressId = useCart().cart.attributes.addressId?.toString();

  const [
    createAddress,
    { loading: createLoading, error: createError }
  ] = useCreateAddressMutation();
  const { data: addresses, loading: getLoading, error: getError } = useAddressesQuery();
  const [editAddress, { loading: editLoading, error: editError }] = useEditAddressMutation();
  const [updateCart, { loading: cartUpdateLoading }] = useUpdateCartMutation();

  useEffect(() => {
    if (createError.network || editError.network) {
      showErrorModal();
      reportError(new Error(createError.network || editError.network));
    }
  }, [createError, editError, showErrorModal]);

  const handleSubmit = useCallback(
    async (addressAttributes: RestAddressAttributesData, id: string | undefined) => {
      if (id) {
        return await editAddress(addressAttributes, id);
      } else {
        return await createAddress(addressAttributes);
      }
    },
    [createAddress, editAddress]
  );

  const handleDefaultAddressChange = useCallback(
    (addressId: string) => {
      updateCart(CartUpdateAction.ShippingAddress, {
        [CartUpdatePropertyName.ShippingAddress]: addressId
      });
    },
    [updateCart]
  );

  if (getError) throwQueryError(getError);

  return (
    <div className="CheckoutShippingAddress">
      {!getLoading && (
        <CheckoutShippingAddressContent
          addresses={addresses.filter(address => address.attributes.source === 'moda')}
          defaultAddressId={defaultAddressId}
          mutationFieldErrors={{ ...createError, ...editError }}
          onDefaultAddressIdChange={handleDefaultAddressChange}
          onSubmit={handleSubmit}
        />
      )}

      <ShippingAddressLoading
        creating={createLoading}
        retrieving={getLoading}
        updating={editLoading || cartUpdateLoading}
      />
    </div>
  );
};
